import React from 'react';
import { 
  BoltIcon, 
  FireIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  ChartBarIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { MeterPoint } from '../../types/meterReading';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';

interface MeterPointsListProps {
  meterPoints: MeterPoint[];
  onRefresh: () => void;
  onEdit?: (meterPoint: MeterPoint) => void;
  onViewReadings?: (meterPoint: MeterPoint) => void;
  onRemove?: (meterPoint: MeterPoint) => void;
}

const MeterPointsList: React.FC<MeterPointsListProps> = ({
  meterPoints,
  onRefresh,
  onEdit,
  onViewReadings,
  onRemove
}) => {
  const getEnergyTypeIcon = (type: string) => {
    return type === 'gas' ? (
      <FireIcon className="h-5 w-5 text-orange-500" />
    ) : (
      <BoltIcon className="h-5 w-5 text-yellow-500" />
    );
  };

  const getEnergyTypeLabel = (type: string) => {
    return type === 'gas' ? 'Gáz' : 'Villany';
  };

  const formatDate = (date: any) => {
    if (!date) return '-';
    
    let dateObj: Date;
    if (date.toDate) {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      return '-';
    }
    
    return format(dateObj, 'yyyy.MM.dd', { locale: hu });
  };

  if (meterPoints.length === 0) {
    return (
      <div className="p-12 text-center">
        <BoltIcon className="h-16 w-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Nincs hozzárendelt POD
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          Kattints a "POD hozzárendelése" gombra egy mérőhely hozzáadásához
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200 dark:divide-gray-700">
      {meterPoints.map((meterPoint) => (
        <div
          key={meterPoint.id}
          className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4 flex-1">
              <div className="flex-shrink-0 mt-1">
                {getEnergyTypeIcon(meterPoint.energyType)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    {meterPoint.podId}
                  </h4>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                    {getEnergyTypeLabel(meterPoint.energyType)}
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    {meterPoint.provider}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Gyártási szám:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">
                      {meterPoint.serialNumber || '-'}
                    </span>
                  </div>
                  
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Szorzó:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">
                      {meterPoint.multiplier}
                    </span>
                  </div>

                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Beszerelés:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">
                      {formatDate(meterPoint.installationDate)}
                    </span>
                  </div>

                  {meterPoint.removalDate && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Leszerelés:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">
                        {formatDate(meterPoint.removalDate)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-4 mt-3">
                  <div className="flex items-center">
                    {meterPoint.isActive ? (
                      <>
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                        <span className="text-xs text-green-700 dark:text-green-400">Aktív</span>
                      </>
                    ) : (
                      <>
                        <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                        <span className="text-xs text-red-700 dark:text-red-400">Inaktív</span>
                      </>
                    )}
                  </div>

                  <div className="flex items-center">
                    {meterPoint.isBillable ? (
                      <>
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                        <span className="text-xs text-green-700 dark:text-green-400">Számlázható</span>
                      </>
                    ) : (
                      <>
                        <XCircleIcon className="h-4 w-4 text-orange-500 mr-1" />
                        <span className="text-xs text-orange-700 dark:text-orange-400">Nem számlázható</span>
                      </>
                    )}
                  </div>
                </div>

                {meterPoint.notes && (
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {meterPoint.notes}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2 ml-4">
              {onViewReadings && (
                <button
                  onClick={() => onViewReadings(meterPoint)}
                  className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  title="Mérőállások megtekintése"
                >
                  <ChartBarIcon className="h-5 w-5" />
                </button>
              )}
              {onEdit && (
                <button
                  onClick={() => onEdit(meterPoint)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  title="Szerkesztés"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
              )}
              {onRemove && (
                <button
                  onClick={() => onRemove(meterPoint)}
                  className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  title="POD eltávolítása"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MeterPointsList;
